/**
 * The CRM lead vocabulary, as label KEYS.
 *
 * What is NOT here, on purpose: a map of which status may follow which. The
 * marketing service owns that state machine (`app/domain/lifecycle.py`
 * LEAD_TRANSITIONS) and returns `allowedNextStatuses` on every lead, computed
 * from it; this screen offers exactly that list. A second copy here would be
 * the "state machine written twice" the CRM gateway refuses to be
 * (`marketing/parsina.bff.crm/src/graphql/resolvers/pipeline.js`), and would
 * show up the first time the two disagreed as a button the server refuses.
 *
 * The vocabularies below are CLOSED sets on the server too; an unknown value
 * arriving from the wire is echoed raw by the screen, never mislabelled.
 */

/** Every lead status, in pipeline order. */
export const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];

/** The two terminal statuses: a closed lead is read-only (the service refuses every edit). */
export const CLOSED_STATUSES = ['won', 'lost'];

export const isClosedStatus = (status) => CLOSED_STATUSES.includes(status);

export const leadStatusKey = (status) => `demoRequests.status.${status}`;

/** Badge tone per status (kit `Badge` variants). Colour always travels with the label. */
export const STATUS_TONE = {
  new: 'warning',
  contacted: 'default',
  qualified: 'default',
  proposal: 'default',
  negotiation: 'default',
  won: 'success',
  lost: 'outline',
};

/** The intent this screen lists. Stamped server-side on every demo request. */
export const DEMO_INTENT = 'demo_request';

/**
 * A patient's «درخواست نوبت» from a doctor's public page
 * (parsinabot.ir/doctors/<slug>; docs/DOCTOR_PUBLIC_PAGE_DESIGN.md §D7). Same
 * lead pipeline, same actions — only what the request carries differs.
 */
export const DOCTOR_PAGE_INTENT = 'doctor_page_appointment';

/** The queues this screen switches between (`?intent=`), in display order. */
export const LEAD_INTENTS = [DEMO_INTENT, DOCTOR_PAGE_INTENT];

export const intentKey = (intent) => `demoRequests.intent.${intent}`;

/** A doctor page's closed contact-channel vocabulary (`app/domain/demo_requests.py`). */
export const PAGE_PREFERRED_CHANNELS = ['call', 'sms', 'whatsapp'];

/**
 * Channels offered when logging a contact — a subset of the service's closed
 * CHANNELS (`app/domain/channels.py`): the ones a sales rep actually uses to
 * reach a dentist who asked for a demo.
 */
export const CONTACT_CHANNELS = ['call', 'whatsapp', 'sms', 'email', 'in_person'];

/** The service's closed INTERACTION_OUTCOMES, in the order a rep thinks of them. */
export const INTERACTION_OUTCOMES = [
  'answered', 'no_answer', 'busy', 'wrong_number', 'voicemail',
  'interested', 'not_interested', 'callback_requested', 'meeting_set',
  'delivered', 'failed', 'other',
];

/**
 * Outcomes that mean a real conversation happened. Only these offer to move a
 * `new` lead to `contacted`: an unanswered or busy call is NOT a contact, and
 * advancing on one would silently stop the «unanswered» clock (§3.2 T4).
 */
export const CONTACT_MADE_OUTCOMES = ['answered', 'interested', 'callback_requested', 'meeting_set'];

export const channelKey = (channel) => `demoRequests.channel.${channel}`;
export const outcomeKey = (outcome) => `demoRequests.outcome.${outcome}`;

/**
 * Preset reasons for closing a lead as lost (§3.2 T8). Stored as the label in
 * the language the rep is working in; a free-text reason is always allowed.
 */
export const LOST_REASON_PRESETS = ['noAnswer', 'otherSoftware', 'price', 'notReady', 'duplicate', 'spam'];
export const lostReasonKey = (preset) => `demoRequests.lost.presets.${preset}`;

/**
 * What a preset STORES, as opposed to what it shows. `lost_reason` is CRM data
 * that reports group by, so it must not depend on the language the rep's panel
 * happens to be in — «قیمت» from one rep and "Price" from another are two
 * reasons in every report. The CRM's working language is Persian, so the stored
 * value is the Persian label whatever the UI shows.
 */
export const LOST_REASON_STORED = Object.freeze({
  // i18n-exempt: stored CRM data (lost_reason), not UI copy — must be locale-independent.
  noAnswer: 'پاسخ نداد',
  // i18n-exempt: stored CRM data (lost_reason), not UI copy — must be locale-independent.
  otherSoftware: 'نرم‌افزار دیگری انتخاب کرد',
  // i18n-exempt: stored CRM data (lost_reason), not UI copy — must be locale-independent.
  price: 'قیمت',
  // i18n-exempt: stored CRM data (lost_reason), not UI copy — must be locale-independent.
  notReady: 'مطب هنوز آماده نیست',
  // i18n-exempt: stored CRM data (lost_reason), not UI copy — must be locale-independent.
  duplicate: 'تکراری',
  // i18n-exempt: stored CRM data (lost_reason), not UI copy — must be locale-independent.
  spam: 'اسپم/درخواست نامعتبر',
});
export const LOST_REASON_MAX = 200;

/** Preset titles for a follow-up (§3.2 T5); a free-text title is always allowed. */
export const FOLLOW_UP_PRESETS = ['demoSession', 'callback'];
export const followUpPresetKey = (preset) => `demoRequests.followUp.presets.${preset}`;
export const FOLLOW_UP_TITLE_MAX = 200;
export const NOTE_MAX = 1000;

/** Demo-request answer vocabularies (`app/domain/demo_requests.py`), for their labels. */
export const PRACTICE_TYPES = ['solo_office', 'group_practice', 'clinic', 'other'];
export const DENTIST_COUNTS = ['1', '2-5', '6-15', '16+'];
export const DEMO_MODES = ['online', 'in_person', 'phone'];
export const PREFERRED_TIMES = ['morning', 'afternoon', 'evening', 'any'];
export const CURRENT_SYSTEMS = ['paper', 'other_software', 'none'];
